// The page a creator opens from their personal link (screen 15). No log-in, in their language.
import type { ApprovalPage, Lang } from "@brandwall/contracts";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, post } from "../api";

type Strings = {
  title: (brand: string, site: string) => string;
  titleInvite: (brand: string) => string;
  shown: (who: string) => string;
  only: (brand: string) => string;
  stop: string;
  terms: (brand: string) => string;
  yes: string;
  no: string;
  who: string;
  future: (brand: string) => string;
  thanks: (name: string, site: string) => string;
  thanksInvite: (name: string, brand: string) => string;
  noThanks: string;
  withdraw: string;
  withdrawn: string;
  by: (brand: string) => string;
  view: string;
  /** The time limit, said before the creator answers (D-099). */
  until: (date: string) => string;
  titleExtend: (brand: string, site: string) => string;
};

const T: Record<Lang, Strings> = {
  en: {
    title: (b, s) => `${b} would like to show your photo on ${s}`,
    titleInvite: (b) => `${b} would like to show your future posts that tag them`,
    shown: (w) => `Shown with your name, ${w}`,
    only: (b) => `Only on ${b}'s own website`,
    stop: "Say stop at any time, and it's removed",
    terms: (b) => `Read ${b}'s full terms`,
    yes: "Yes, I agree",
    no: "No thanks",
    who: "Your Instagram username",
    future: (b) => `Also show my future posts that tag ${b}, without asking each time`,
    thanks: (n, s) =>
      `Thank you${n ? `, ${n}` : ""}! Your photo can now appear on ${s}. You can say stop at any time from this link.`,
    thanksInvite: (n, b) =>
      `Thank you${n ? `, ${n}` : ""}! ${b} can now show your posts that tag them. You can say stop at any time from this link.`,
    noThanks: "No problem. Your answer is saved, and your photo won't be shown.",
    withdraw: "Stop showing my photo",
    withdrawn: "Done. Your photo is no longer shown, and it leaves the website within 24 hours.",
    by: (b) => `${b} uses Brandwall to ask for permission`,
    view: "See the post on Instagram",
    until: (d) => `Your post may be shown on our website until ${d}.`,
    titleExtend: (b, s) => `${b} would like to keep showing your photo on ${s}`,
  },
  da: {
    title: (b, s) => `${b} vil gerne vise dit billede på ${s}`,
    titleInvite: (b) => `${b} vil gerne vise dine kommende opslag, hvor du tagger dem`,
    shown: (w) => `Vises med dit navn, ${w}`,
    only: (b) => `Kun på ${b}s egen hjemmeside`,
    stop: "Sig stop når som helst, så bliver det fjernet",
    terms: (b) => `Læs ${b}s fulde vilkår`,
    yes: "Ja, det er i orden",
    no: "Nej tak",
    who: "Dit Instagram-brugernavn",
    future: (b) => `Vis også mine kommende opslag, hvor jeg tagger ${b}, uden at spørge hver gang`,
    thanks: (n, s) =>
      `Tusind tak${n ? `, ${n}` : ""}! Dit billede kan nu blive vist på ${s}. Du kan altid sige stop via dette link.`,
    thanksInvite: (n, b) =>
      `Tusind tak${n ? `, ${n}` : ""}! ${b} kan nu vise dine opslag, hvor du tagger dem. Du kan altid sige stop via dette link.`,
    noThanks: "Helt i orden. Dit svar er gemt, og dit billede bliver ikke vist.",
    withdraw: "Stop med at vise mit billede",
    withdrawn:
      "Sådan. Dit billede bliver ikke vist længere og forsvinder fra hjemmesiden inden for et døgn.",
    by: (b) => `${b} bruger Brandwall til at spørge om lov`,
    view: "Se opslaget på Instagram",
    until: (d) => `Dit opslag må vises på vores hjemmeside indtil ${d}.`,
    titleExtend: (b, s) => `${b} vil gerne blive ved med at vise dit billede på ${s}`,
  },
  sv: {
    title: (b, s) => `${b} vill gärna visa din bild på ${s}`,
    titleInvite: (b) => `${b} vill gärna visa dina kommande inlägg där du taggar dem`,
    shown: (w) => `Visas med ditt namn, ${w}`,
    only: (b) => `Bara på ${b}s egen webbplats`,
    stop: "Säg stopp när som helst, så tas den bort",
    terms: (b) => `Läs ${b}s fullständiga villkor`,
    yes: "Ja, det går bra",
    no: "Nej tack",
    who: "Ditt användarnamn på Instagram",
    future: (b) => `Visa även mina kommande inlägg där jag taggar ${b}, utan att fråga varje gång`,
    thanks: (n, s) =>
      `Tack${n ? `, ${n}` : ""}! Din bild kan nu visas på ${s}. Du kan säga stopp när som helst via den här länken.`,
    thanksInvite: (n, b) =>
      `Tack${n ? `, ${n}` : ""}! ${b} kan nu visa dina inlägg där du taggar dem. Du kan säga stopp när som helst via den här länken.`,
    noThanks: "Inga problem. Ditt svar är sparat och din bild visas inte.",
    withdraw: "Sluta visa min bild",
    withdrawn: "Klart. Din bild visas inte längre och försvinner från webbplatsen inom ett dygn.",
    by: (b) => `${b} använder Brandwall för att be om lov`,
    view: "Se inlägget på Instagram",
    until: (d) => `Ditt inlägg får visas på vår webbplats fram till ${d}.`,
    titleExtend: (b, s) => `${b} vill gärna fortsätta visa din bild på ${s}`,
  },
  no: {
    title: (b, s) => `${b} vil gjerne vise bildet ditt på ${s}`,
    titleInvite: (b) => `${b} vil gjerne vise de neste innleggene der du tagger dem`,
    shown: (w) => `Vises med navnet ditt, ${w}`,
    only: (b) => `Bare på ${b}s egen nettside`,
    stop: "Si stopp når som helst, så blir det fjernet",
    terms: (b) => `Les ${b}s fullstendige vilkår`,
    yes: "Ja, det er greit",
    no: "Nei takk",
    who: "Brukernavnet ditt på Instagram",
    future: (b) => `Vis også de neste innleggene mine der jeg tagger ${b}, uten å spørre hver gang`,
    thanks: (n, s) =>
      `Tusen takk${n ? `, ${n}` : ""}! Bildet ditt kan nå vises på ${s}. Du kan si stopp når som helst via denne lenken.`,
    thanksInvite: (n, b) =>
      `Tusen takk${n ? `, ${n}` : ""}! ${b} kan nå vise innleggene der du tagger dem. Du kan si stopp når som helst via denne lenken.`,
    noThanks: "Helt greit. Svaret ditt er lagret, og bildet ditt blir ikke vist.",
    withdraw: "Slutt å vise bildet mitt",
    withdrawn: "Ferdig. Bildet ditt vises ikke lenger og forsvinner fra nettsiden innen et døgn.",
    by: (b) => `${b} bruker Brandwall til å spørre om lov`,
    view: "Se innlegget på Instagram",
    until: (d) => `Innlegget ditt kan vises på nettsiden vår fram til ${d}.`,
    titleExtend: (b, s) => `${b} vil gjerne fortsette å vise bildet ditt på ${s}`,
  },
  fi: {
    title: (b, s) => `${b} haluaisi näyttää kuvasi sivustolla ${s}`,
    titleInvite: (b) => `${b} haluaisi näyttää tulevat julkaisusi, joissa merkitset heidät`,
    shown: (w) => `Näytetään nimelläsi, ${w}`,
    only: (b) => `Vain ${b}n omalla sivustolla`,
    stop: "Voit perua milloin tahansa, ja kuva poistetaan",
    terms: (b) => `Lue ${b}n ehdot kokonaan`,
    yes: "Kyllä, sopii",
    no: "Ei kiitos",
    who: "Instagram-käyttäjänimesi",
    future: (b) => `Näytä myös tulevat julkaisuni, joissa merkitsen ${b}n, kysymättä joka kerta`,
    thanks: (n, s) =>
      `Kiitos${n ? `, ${n}` : ""}! Kuvasi voi nyt näkyä sivustolla ${s}. Voit perua luvan milloin tahansa tästä linkistä.`,
    thanksInvite: (n, b) =>
      `Kiitos${n ? `, ${n}` : ""}! ${b} voi nyt näyttää julkaisusi, joissa merkitset heidät. Voit perua luvan milloin tahansa tästä linkistä.`,
    noThanks: "Ei haittaa. Vastauksesi on tallennettu, eikä kuvaasi näytetä.",
    withdraw: "Lopeta kuvani näyttäminen",
    withdrawn: "Valmis. Kuvaasi ei enää näytetä, ja se poistuu sivustolta vuorokauden kuluessa.",
    by: (b) => `${b} kysyy lupaa Brandwallin kautta`,
    view: "Katso julkaisu Instagramissa",
    until: (d) => `Julkaisuasi voidaan näyttää verkkosivustollamme ${d} asti.`,
    titleExtend: (b, s) => `${b} haluaisi jatkaa kuvasi näyttämistä sivustolla ${s}`,
  },
};

const NAMES: Record<Lang, string> = {
  en: "English",
  da: "Dansk",
  sv: "Svenska",
  no: "Norsk",
  fi: "Suomi",
};

const LOCALES: Record<Lang, string> = {
  en: "en-GB",
  da: "da-DK",
  sv: "sv-SE",
  no: "nb-NO",
  fi: "fi-FI",
};

/** "31 March 2027", in the creator's language. */
function dateIn(date: string, lang: Lang) {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(LOCALES[lang], {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function first(username: string) {
  const p = username.split(/[._\d]/).find((x) => x.length > 1) ?? username;
  return p ? p[0]!.toUpperCase() + p.slice(1) : "";
}

export function Approval() {
  const { token } = useParams();
  const [page, setPage] = useState<ApprovalPage | null>(null);
  const [lang, setLang] = useState<Lang>("en");
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [future, setFuture] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<"yes" | "no" | "withdrawn" | null>(null);

  useEffect(() => {
    api<ApprovalPage>(`/v1/public/r/${token}`)
      .then((p) => {
        setPage(p);
        setLang(p.language);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "This link isn't valid."));
  }, [token]);

  useEffect(() => {
    document.documentElement.lang = lang === "no" ? "nb" : lang;
  }, [lang]);

  const answer = async (a: "yes" | "no") => {
    setBusy(true);
    try {
      const p = await post<ApprovalPage>(`/v1/public/r/${token}/answer`, {
        answer: a,
        ...(page?.creatorKnown || !username.trim() ? {} : { username }),
        joinTrusted: a === "yes" && future,
      });
      setPage(p);
      setSaid(a);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const withdraw = async () => {
    setBusy(true);
    try {
      setPage(await post<ApprovalPage>(`/v1/public/r/${token}/withdraw`, {}));
      setSaid("withdrawn");
    } finally {
      setBusy(false);
    }
  };

  const t = T[lang];
  if (error && !page) {
    return (
      <div className="public">
        <div className="sheet">
          <p>{error}</p>
        </div>
      </div>
    );
  }
  if (!page) return <div className="public muted">…</div>;
  const who = page.creator || username.replace(/^@/, "");
  const name = first(who);
  const invite = page.kind === "trusted_invite";
  const open = ["draft", "pending", "replied", "expired"].includes(page.status);

  return (
    <div className="public">
      <main className="sheet" aria-live="polite">
        <div className="row" style={{ justifyContent: "center" }}>
          {page.brand.logoUrl ? (
            <img
              src={page.brand.logoUrl}
              alt={page.brand.name}
              style={{ maxHeight: 36, maxWidth: 180 }}
            />
          ) : (
            <b style={{ letterSpacing: "0.1em", fontSize: 18 }}>{page.brand.name.toUpperCase()}</b>
          )}
        </div>
        <h1 style={{ fontSize: 22, textAlign: "center" }}>
          {invite
            ? t.titleInvite(page.brand.name)
            : page.extend
              ? t.titleExtend(page.brand.name, page.brand.site)
              : t.title(page.brand.name, page.brand.site)}
        </h1>
        {page.imageUrl && <img className="photo" src={page.imageUrl} alt="" />}

        {open && (
          <>
            <ul className="checks">
              <li>{t.shown(who ? `@${who}` : t.who.toLowerCase())}</li>
              <li>{t.only(page.brand.name)}</li>
              {page.showUntil && <li>{t.until(dateIn(page.showUntil, lang))}</li>}
              <li>{t.stop}</li>
            </ul>
            {page.termsUrl && (
              <a href={page.termsUrl} target="_blank" rel="noreferrer" style={{ fontWeight: 700 }}>
                {t.terms(page.brand.name)}
              </a>
            )}
            {!page.creatorKnown && (
              <label className="field">
                {t.who}
                <input
                  className="input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="@"
                  autoComplete="username"
                />
              </label>
            )}
            {!invite && (
              <label className="row small" style={{ fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={future}
                  onChange={(e) => setFuture(e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                {t.future(page.brand.name)}
              </label>
            )}
            {error && (
              <div className="error" role="alert">
                {error}
              </div>
            )}
            <button
              type="button"
              className="btn ok block"
              disabled={busy || (!page.creatorKnown && !username.trim())}
              onClick={() => answer("yes")}
            >
              {t.yes}
            </button>
            <button
              type="button"
              className="btn secondary block"
              disabled={busy}
              onClick={() => answer("no")}
            >
              {t.no}
            </button>
          </>
        )}

        {page.status === "granted" && (
          <>
            <div className="notice">
              {invite ? t.thanksInvite(name, page.brand.name) : t.thanks(name, page.brand.site)}
            </div>
            <button
              type="button"
              className="btn link small"
              disabled={busy}
              onClick={withdraw}
              style={{ justifySelf: "center" }}
            >
              {t.withdraw}
            </button>
          </>
        )}
        {page.status === "declined" && <div className="quote">{t.noThanks}</div>}
        {page.status === "withdrawn" && (
          <div className="quote">{said === "withdrawn" ? t.withdrawn : t.noThanks}</div>
        )}

        {page.permalink && (
          <a
            className="small muted"
            href={page.permalink}
            target="_blank"
            rel="noreferrer"
            style={{ textAlign: "center" }}
          >
            {t.view}
          </a>
        )}
        <div className="lang" role="group" aria-label="Language">
          {(Object.keys(NAMES) as Lang[]).map((l) => (
            <button
              key={l}
              type="button"
              aria-pressed={l === lang}
              onClick={() => setLang(l)}
              lang={l}
            >
              {NAMES[l]}
            </button>
          ))}
        </div>
        <p className="small muted" style={{ textAlign: "center" }}>
          {t.by(page.brand.name)}
        </p>
      </main>
    </div>
  );
}
